import { useState } from "react";
import "./brief.css";

// Mirrors ResultsGrid.jsx's matchTier()/normalizePercent() thresholds, kept as
// a local copy rather than a shared import because BriefPickCard consumes a
// differently-shaped candidate object (grouped by need, carries a rationale +
// thumbnail). The tier is always a word; the badge tone only reinforces it:
// success = strong, accent = good, warning = possible.
function matchTier(pct) {
  if (pct >= 58) return { label: "Strong match", tone: "success" };
  if (pct >= 51) return { label: "Good match", tone: "accent" };
  return { label: "Possible match", tone: "warning" };
}

function normalizePercent(score) {
  if (score == null || Number.isNaN(Number(score))) return 0;
  const n = Number(score);
  if (n <= 1 && n >= 0) return Math.round(n * 100);
  return Math.min(100, Math.max(0, Math.round(n)));
}

function Icon({ children, directional = false }) {
  return (
    <svg
      className={directional ? "gd-icon gd-icon--directional" : "gd-icon"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Quiet placeholder shown when a frame has no thumbnail (missing or failed to load). */
function ThumbnailFallback() {
  return (
    <div aria-hidden className="brief-pick__thumb">
      <Icon>
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
        <circle cx="9" cy="9" r="2" />
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
      </Icon>
    </div>
  );
}

/**
 * One picked reference frame for a need: candidate details plus the judge's
 * rationale for choosing it. `pick` is one entry of a needResult's `picks` array:
 * { candidate: { frameName, fileName, pageName, teamName, similarityScore,
 *   figmaLink, sourceVariant, thumbnailUrl }, rationale }.
 */
export default function BriefPickCard({ pick }) {
  const [imgFailed, setImgFailed] = useState(false);

  const candidate = pick?.candidate || {};
  const rationale = typeof pick?.rationale === "string" ? pick.rationale.trim() : "";

  const frameName = candidate.frameName?.trim() || "Untitled frame";
  const pageName = candidate.pageName?.trim() || "—";
  const fileName = candidate.fileName ?? "—";
  const teamName = candidate.teamName ?? "";
  const figmaLink = candidate.figmaLink ?? "#";
  const thumbnailUrl = candidate.thumbnailUrl;

  const hasScore = candidate.similarityScore != null && !Number.isNaN(Number(candidate.similarityScore));
  const pct = hasScore ? normalizePercent(candidate.similarityScore) : 0;
  const tier = hasScore ? matchTier(pct) : null;

  // A missing/null thumbnailUrl (thumbnail fetch failed server-side, or the
  // field is simply absent) and an <img> load error both fall back to the
  // same quiet placeholder — a broken thumbnail must never break the layout
  // or read as an error state.
  const showImage = !!thumbnailUrl && !imgFailed;

  return (
    <article className="brief-pick">
      {showImage ? (
        <div className="brief-pick__thumb">
          <img
            className="brief-pick__img"
            src={thumbnailUrl}
            alt=""
            aria-hidden
            onError={() => setImgFailed(true)}
          />
        </div>
      ) : (
        <ThumbnailFallback />
      )}

      <div className="brief-pick__body">
        <h4 title={frameName} className="body-strong brief-pick__title">
          {frameName}
        </h4>

        <p className="small gd-muted brief-wrap">{pageName}</p>

        <p className="small gd-muted brief-pick__file">
          <Icon>
            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
            <path d="M14 2v4a2 2 0 0 0 2 2h4" />
          </Icon>
          <span className="brief-wrap">{fileName}</span>
        </p>

        <div className="gd-row gd-row--wrap">
          <span className="gd-badge brief-pick__team" title={teamName}>
            {teamName || "Team"}
          </span>

          {hasScore && tier && (
            <span
              className={`gd-badge gd-badge--${tier.tone}`}
              aria-label={`${tier.label} — ${pct}% similarity`}
            >
              {tier.label} · {pct}%
            </span>
          )}
        </div>

        {rationale ? <p className="small brief-pick__rationale">{rationale}</p> : null}

        <a
          className="gd-btn gd-btn--block brief-pick__open"
          href={figmaLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Figma
          <Icon directional>
            <path d="M15 3h6v6" />
            <path d="M10 14 21 3" />
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          </Icon>
        </a>
      </div>
    </article>
  );
}
